import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Lang } from '@app/shared';
import type { CalendarDayDto, CalendarItemDto } from '../../api/cabinetTypes';
import { useAppApi } from '../../app/ApiProvider';
import {
  dayKeyInTz,
  dayKeyLabel,
  formatDateInTz,
  formatTimeInTz,
  parseDayKey,
  type DayKey,
} from '../../domain/datetime';
import { useAsync } from '../../hooks/useAsync';
import { isApiFailure } from '../../i18n/errors';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { CtaButton, SmallButton } from '../../ui/Cta';
import { LangSwitch, Tag } from '../../ui/controls';
import { EmptyState, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, TopBar } from '../../ui/Screen';
import { useBusiness } from './useBusiness';
import {
  actionsFor,
  calendarTagKey,
  calendarTone,
  dayNavigation,
  sortByStart,
  statTiles,
  type ActionSpec,
  type CalendarAction,
  type CalendarTone,
  type StatTile,
} from './calendar/calendarDay';

/**
 * Календарь бизнеса со статусами и статистикой (ТЗ §14.1, §14.2; экран `s-cal` прототипа).
 *
 * Весь день приходит ОДНИМ запросом `getCalendarDay`: и плитки, и список. Считать плитки
 * на фронте по видимым записям нельзя — загрузка требует рабочих часов и отпусков, которых
 * в выдаче нет, а «неявок 0%» тогда рисовалось бы и у выходного дня.
 *
 * После любого действия день перечитывается ЦЕЛИКОМ, а не правится карточка на месте:
 * плитки считаются по всей выборке, и локальная правка развела бы список со статистикой —
 * запись показана завершённой, а «Записей» и «Неявки» остались прежними.
 *
 * Чья это организация — решает подпись `initData`; `businessId` только адресует строку.
 */
export function CalendarScreen(props: { businessId: string; onBack: () => void }): JSX.Element {
  const { businessId } = props;
  const { t, lang } = useI18n();
  const api = useAppApi();

  // Организация нужна ради двух вещей: названия в шапке и `tz`, по которому считается
  // «сегодня». До её загрузки день не выбирается вовсе — взять день из часов телефона
  // значило бы открыть владельцу вчерашний календарь (AGENTS.md, правило 1).
  const biz = useBusiness(businessId);
  const business = biz.business;

  const today = useMemo<DayKey | null>(
    () => (business ? dayKeyInTz(new Date(), business.tz) : null),
    [business],
  );

  const [day, setDay] = useState<DayKey | null>(null);
  useEffect(() => {
    if (today) setDay((current) => current ?? today);
  }, [today]);

  const load = useAsync<CalendarDayDto | null>(
    useCallback(async () => (day ? api.cabinet.getCalendarDay(businessId, day) : null), [
      api,
      businessId,
      day,
    ]),
    [businessId, day],
  );

  /**
   * Запрошенное подтверждение. Тексты шторки лежат ЗДЕСЬ, а не достаются из `spec` при
   * отрисовке: у `spec.confirm` тип «может быть null», и в шторке он потребовал бы ветку
   * «а если подтверждения нет» — то есть заголовок-заглушку у операции, которая уже
   * спрашивает разрешения. Кладём в состояние только то, что подтверждения требует.
   */
  const [pending, setPending] = useState<{
    item: CalendarItemDto;
    action: CalendarAction;
    variant: ActionSpec['variant'];
    confirm: NonNullable<ActionSpec['confirm']>;
  } | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** Запись, по которой владелец нажал «Пришёл» до начала визита (ТЗ §19, №28). */
  const [earlyCompleteId, setEarlyCompleteId] = useState<string | null>(null);

  const dayData = load.state.status === 'ready' ? load.state.data : null;
  const items = useMemo(() => (dayData ? sortByStart(dayData.items) : []), [dayData]);

  /**
   * Каждая кнопка зовёт СВОЙ метод API — как и на бэкенде, где у каждой свой маршрут.
   * Общий вызов с параметром-действием позволял бы промахнуться значением там, где промах
   * означает бан клиента на платформе (ТЗ §10.2).
   */
  const run = async (
    action: CalendarAction,
    appointmentId: string,
    confirmedEarly = false,
  ): Promise<void> => {
    setBusyId(appointmentId);
    setActionError(null);
    try {
      switch (action) {
        case 'complete':
          await api.cabinet.completeAppointment(
            businessId,
            appointmentId,
            confirmedEarly ? { confirmedEarly: true } : undefined,
          );
          break;
        case 'cancel':
          await api.cabinet.cancelAppointment(businessId, appointmentId);
          break;
        case 'noShow':
          await api.cabinet.markNoShow(businessId, appointmentId);
          break;
        case 'forgive':
          await api.cabinet.forgiveNoShow(businessId, appointmentId);
          break;
      }
      setPending(null);
      load.reload();
    } catch (error) {
      setPending(null);
      // Визит ещё не начался — это вопрос, а не отказ (ТЗ §19, №28). Промах по «Пришёл»
      // на будущей записи закрывает её: клиент сможет оценить визит, которого не было,
      // а если он потом не придёт — отмечать неявку будет уже не на чем.
      if (isApiFailure(error) && error.key === 'err.completeTooEarly') {
        setEarlyCompleteId(appointmentId);
      } else {
        setActionError(error);
      }
    } finally {
      setBusyId(null);
    }
  };

  const confirmEarlyComplete = (): void => {
    const id = earlyCompleteId;
    setEarlyCompleteId(null);
    if (id) void run('complete', id, true);
  };

  const onAction = (item: CalendarItemDto, spec: ActionSpec): void => {
    // Подтверждение спрашивается у всех действий, кроме «Пришёл» (см. ACTION_SPECS).
    if (spec.confirm) {
      setPending({ item, action: spec.action, variant: spec.variant, confirm: spec.confirm });
    } else {
      void run(spec.action, item.id);
    }
  };

  return (
    <Screen>
      <TopBar
        title={t('calTitle')}
        subtitle={business?.name}
        onBack={props.onBack}
        right={<LangSwitch />}
      />

      {business && day && today ? (
        <DayBar day={day} today={today} lang={lang} onPick={setDay} />
      ) : null}

      <ScrollArea bottomGap="tabs">
        {biz.state.status === 'loading' ? <Loader /> : null}
        {biz.state.status === 'failed' ? (
          <ErrorState error={biz.state.error} onRetry={biz.reload} />
        ) : null}

        {business ? (
          <>
            {/* `day === null` — организация уже загружена, а день ещё не выбран (эффект
                отработает следующим тактом). Это тоже загрузка, и без явной ветки экран
                моргнул бы пустотой между двумя запросами. */}
            {load.state.status === 'loading' || day === null ? <Loader /> : null}
            {load.state.status === 'failed' ? (
              <ErrorState error={load.state.error} onRetry={load.reload} />
            ) : null}

            {dayData ? (
              <>
                <Stats tiles={statTiles(dayData.stats)} />

                <div className="px-4 pb-2 pt-2">
                  {items.length === 0 ? (
                    <EmptyState>{t('calEmpty')}</EmptyState>
                  ) : (
                    items.map((item) => (
                      <AppointmentRow
                        key={item.id}
                        item={item}
                        // Пояс берётся из ОТВЕТА (`day.tz`), а не из строки организации:
                        // ответ внутренне непротиворечив — по этому же поясу сервер
                        // разворачивал границы суток и отбирал записи.
                        tz={dayData.tz}
                        busy={busyId === item.id}
                        onAction={(spec) => onAction(item, spec)}
                      />
                    ))
                  )}
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </ScrollArea>

      {/* Подтверждение. Текст зависит от кнопки: отмена шлёт уведомление, неявка двигает
          шкалу санкций, «Снять отметку» откатывает бан (ТЗ §10.2, §10.4). */}
      <BottomSheet
        open={pending !== null}
        title={pending ? t(pending.confirm.titleKey) : undefined}
        onClose={() => setPending(null)}
      >
        {pending ? (
          <>
            <div className="mb-4 text-[13px] leading-[1.5] text-ink-2">
              {t(pending.confirm.bodyKey)}
            </div>
            <div className="flex gap-2">
              <CtaButton variant="ghost" onClick={() => setPending(null)}>
                {t('confirmNo')}
              </CtaButton>
              <CtaButton
                // «Снять отметку» возвращает клиенту право записи — это не разрушительное
                // действие, и красная кнопка врала бы о его смысле.
                variant={pending.variant === 'accent' ? 'primary' : 'danger'}
                disabled={busyId === pending.item.id}
                onClick={() => void run(pending.action, pending.item.id)}
              >
                {t('confirmYes')}
              </CtaButton>
            </div>
          </>
        ) : null}
      </BottomSheet>

      {/* Ранний приход: визит ещё не начался, поэтому спрашиваем, а не отказываем
          (ТЗ §19, №28). Обычное «Пришёл» подтверждения не требует — только это. */}
      <BottomSheet
        open={earlyCompleteId !== null}
        title={t('calEarlyTitle')}
        onClose={() => setEarlyCompleteId(null)}
      >
        <div className="mb-4 text-[13px] leading-[1.5] text-ink-2">{t('calEarlyBody')}</div>
        <div className="flex gap-2">
          <CtaButton variant="ghost" onClick={() => setEarlyCompleteId(null)}>
            {t('confirmNo')}
          </CtaButton>
          <CtaButton onClick={confirmEarlyComplete}>{t('calEarlyYes')}</CtaButton>
        </div>
      </BottomSheet>

      <BottomSheet
        open={actionError !== null}
        title={t('errTitle')}
        onClose={() => setActionError(null)}
      >
        {/* Ошибка бэкенда приходит КЛЮЧОМ i18n; текст собирает ErrorState по словарю. */}
        <ErrorState error={actionError} withTitle={false} />
        <CtaButton variant="ghost" onClick={() => setActionError(null)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>
    </Screen>
  );
}

/**
 * Переключатель дня: «‹ / дата / ›» плюс возврат на сегодня.
 *
 * Кнопка «Сегодня» появляется только когда открыт другой день. Постоянная кнопка,
 * не делающая ничего на сегодняшнем дне, читается как сломанная.
 */
function DayBar(props: {
  day: DayKey;
  today: DayKey;
  lang: Lang;
  onPick: (day: DayKey) => void;
}): JSX.Element {
  const { t } = useI18n();
  const nav = dayNavigation(props.day, props.today);
  const { weekday } = dayKeyLabel(props.day, props.lang);

  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-hair px-4 py-2.5">
      <ArrowButton label={t('calPrevDay')} onClick={() => props.onPick(nav.prev)}>
        ‹
      </ArrowButton>

      <div className="min-w-0 flex-1 text-center">
        <div className="truncate text-[14px] font-bold tracking-tight">
          {dayTitle(props.day, props.lang)}
        </div>
        <div className="truncate text-[11.5px] text-ink-3">
          {weekday}
          {nav.relativeKey ? ` · ${t(nav.relativeKey)}` : ''}
        </div>
      </div>

      <ArrowButton label={t('calNextDay')} onClick={() => props.onPick(nav.next)}>
        ›
      </ArrowButton>

      {nav.isToday ? null : (
        <button
          type="button"
          onClick={() => props.onPick(props.today)}
          className="shrink-0 rounded-[9px] bg-surface-2 px-2.5 py-1.5 text-[11.5px] font-bold text-accent"
        >
          {t('calToday')}
        </button>
      )}
    </div>
  );
}

function ArrowButton(props: {
  label: string;
  onClick: () => void;
  children: string;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={props.label}
      onClick={props.onClick}
      className="shrink-0 rounded-[9px] bg-surface-2 px-3 py-1 text-[20px] leading-[1.2] text-accent"
    >
      {props.children}
    </button>
  );
}

/**
 * Дата в подписи дня.
 *
 * `DayKey` уже посчитан в поясе организации, поэтому якорь форматируется по UTC: второй
 * пересчёт поясом сместил бы подпись на сутки у дней, начинающихся до полуночи UTC
 * (то же соображение, что у `dayKeyLabel`). Полдень как якорь не попадает ни в один
 * перевод стрелок.
 */
function dayTitle(key: DayKey, lang: Lang): string {
  const { year, month, day } = parseDayKey(key);
  return formatDateInTz(new Date(Date.UTC(year, month - 1, day, 12)), 'UTC', lang);
}

const STAT_TONE: Readonly<Record<StatTile['tone'], string>> = {
  accent: 'text-accent',
  green: 'text-green',
  danger: 'text-danger',
  gold: 'text-gold',
};

/** Пять плиток прототипа: `stats3` сверху, `stats2` под ними. */
function Stats(props: { tiles: readonly StatTile[] }): JSX.Element {
  const top = props.tiles.slice(0, 3);
  const bottom = props.tiles.slice(3);
  return (
    <>
      <div className="grid grid-cols-3 gap-2 px-4 pb-1 pt-3.5">
        {top.map((tile) => (
          <StatBox key={tile.labelKey} tile={tile} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 px-4 pb-1 pt-2">
        {bottom.map((tile) => (
          <StatBox key={tile.labelKey} tile={tile} />
        ))}
      </div>
    </>
  );
}

function StatBox(props: { tile: StatTile }): JSX.Element {
  const { t } = useI18n();
  const { tile } = props;

  return (
    <div className="rounded-[13px] bg-surface px-1.5 py-3 text-center shadow-[inset_0_0_0_1px_var(--hair)]">
      {/* Прочерк красится нейтральным, а не цветом показателя: зелёное «—» на месте
          загрузки читалось бы как значение, которого нет. */}
      <div
        className={`text-[19px] font-extrabold tracking-[-0.02em] ${
          tile.text === null ? 'text-ink-3' : STAT_TONE[tile.tone]
        }`}
      >
        {tile.text ?? t('stNone')}
      </div>
      <div className="mt-[3px] text-[10px] leading-[1.3] text-ink-3">{t(tile.labelKey)}</div>
    </div>
  );
}

/** Цвет левой полосы карточки — тот же признак, что и у бейджа, только читаемый боковым зрением. */
const CARD_BORDER: Readonly<Record<CalendarTone, string>> = {
  new: 'border-l-gold',
  ok: 'border-l-accent',
  done: 'border-l-green opacity-90',
  cancelled: 'border-l-hair opacity-75',
  noshow: 'border-l-danger opacity-90',
};

function AppointmentRow(props: {
  item: CalendarItemDto;
  tz: string;
  busy: boolean;
  onAction: (spec: ActionSpec) => void;
}): JSX.Element {
  const { t, lang } = useI18n();
  const { item } = props;
  const tone = calendarTone(item.status);
  const actions = actionsFor(item.status);

  return (
    <div
      className={`mb-2.5 flex gap-3 rounded-card border-l-[3px] bg-surface px-3.5 py-3.5 shadow-[inset_0_0_0_1px_var(--hair)] ${CARD_BORDER[tone]}`}
    >
      <div className="min-w-[46px] shrink-0">
        <div className="text-[15px] font-extrabold tracking-[-0.02em]">
          {formatTimeInTz(item.startsAt, props.tz, lang)}
        </div>
        <div className="mt-0.5 text-[10px] font-semibold text-ink-3">
          {item.service.durationMin} {t('min')}
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2.5">
          <div className="min-w-0">
            {/* Снимок имени на момент записи — единственное, что бизнес знает о клиенте:
                контактов и `clientTgId` в DTO нет (AGENTS.md, правило 3; ТЗ §9.3). */}
            <div className="truncate text-[14px] font-bold">{item.clientName}</div>
            <div className="mt-0.5 truncate text-[12.5px] text-ink-3">
              {lang === 'ru' ? item.service.titleRu : item.service.titleUz} · {item.staff.name}
            </div>
          </div>
          <Tag tone={tone}>{t(calendarTagKey(item))}</Tag>
        </div>

        {actions.length > 0 ? (
          <div className="mt-2.5 flex gap-2">
            {actions.map((spec) => (
              <SmallButton
                key={spec.action}
                variant={spec.variant}
                disabled={props.busy}
                onClick={() => props.onAction(spec)}
              >
                {t(spec.labelKey)}
              </SmallButton>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
